<template>
    <div class="student-analysis-filter col">
        <div
            :class="{ 'selected-filters-container': enableAutoScroll }"
            v-if="shouldShowPins()"
            ref="selectedFiltersContainer"
        >
            <div class="selected-filters-row row">
                <div :key="tag.id" v-for="tag in filter.tags" class="selected-tag">
                    <span class="selected-tag-name">{{ tag.name }}</span>
                    <button
                        class="selected-tag-cancel"
                        @click.stop="onSelectedTagRemoved(tag)"
                        :aria-label="`Remove filter - ${tag.name}`"
                    >
                        ✕
                    </button>
                </div>

                <!-- Other filters using computed selected tags -->
                <div
                    v-for="selectedTag in activeFilterTags"
                    :key="selectedTag.key"
                    class="selected-tag"
                >
                    <span class="selected-tag-name">{{ selectedTag.label }}</span>
                    <button
                        class="selected-tag-cancel"
                        @click.stop="selectedTag.removeHandler"
                        :aria-label="`Remove filter - ${selectedTag.label}`"
                    >
                        ✕
                    </button>
                </div>
            </div>
        </div>
        <div class="student-filters">
            <div class="student-filters__filters">
                <div class="filters-row row">
                    <div v-if="showStudentSearch" class="input-container">
                        <BaseInputText
                            v-model="filter.search"
                            :placeholder="
                                isExperimentalAccessibilityEnabled ? '' : 'Search a student name'
                            "
                            :label="
                                isExperimentalAccessibilityEnabled
                                    ? 'Search by Student Name'
                                    : 'null'
                            "
                            aria-label="Search by student name"
                            @input="executeFilter"
                            hideLabel
                        />
                        <BaseIcon icon="search" class="icon-search" />
                    </div>
                    <div v-if="showTagFilter" class="input-container" @click.stop>
                        <BaseTreeSelect
                            :modelValue="selectedTagValues"
                            :options="tagCategoriesList"
                            label="Filter by Tag"
                            placeholder="Filter by Tag"
                            optionLabel="name"
                            optionValue="id"
                            optionGroupLabel="name"
                            optionGroupValue="id"
                            optionGroupChildren="tagsRef"
                            :selectionMode="singleSelectTags ? 'single' : 'checkbox'"
                            :asyncFilter="true"
                            :optionsLoading="isLoadingTags"
                            hideLabel
                            showClear
                            @handleFilter="onTagSearch"
                            @loadOptionsNextPage="onTagCategoriesNextPage"
                            @update:modelValue="onTagTreeSelectChange"
                            appendTo="body"
                        />
                    </div>
                    <div
                        v-if="enableRetentionFeatures && showRiskLevelFilter"
                        class="input-container"
                        @click.stop
                    >
                        <BaseSelect
                            :modelValue="filter.riskLevel"
                            :options="riskLevels"
                            optionLabel="name"
                            optionValue="id"
                            :label="riskLevelFilterLabel"
                            :placeholder="riskLevelFilterLabel"
                            showClear
                            @update:modelValue="onRiskLevelClick"
                            hideLabel
                        />
                    </div>
                    <div
                        v-if="
                            enableRetentionFeatures &&
                            (showRiskDriverFilter || showRiskSubDriverFilter)
                        "
                        class="input-container"
                        @click.stop
                    >
                        <BaseTreeSelect
                            :modelValue="selectedRiskValues"
                            :options="riskDriversTreeData"
                            label="Filter by Risk Driver"
                            placeholder="Filter by Risk Driver"
                            selectionMode="checkbox"
                            showClear
                            @update:modelValue="onRiskTreeSelectChange"
                            selectionModeStrict
                            appendTo="body"
                            hideLabel
                        />
                    </div>
                </div>
                <div class="row" v-if="showMoreFilters">
                    <div v-if="showActionTakenFilter" class="input-container" @click.stop>
                        <template v-if="isExperimentalAccessibilityEnabled">
                            <DropdownV2
                                ref="actionTakenFilter"
                                :options="actionTakenOpts"
                                :selectedOptionValue="filter.actionTaken"
                                optionLabelKey="key"
                                optionValueKey="value"
                                label="Filter by Action Taken"
                                @onChange="(option) => onActionTakenClick(option.value)"
                            />
                        </template>
                        <template v-else>
                            <button
                                class="input-group cursor-pointer"
                                @click.stop="toggleDropdown('actionTaken')"
                                ref="actionTakenFilter"
                            >
                                <label class="input-label faded"> Filter by Action Taken </label>
                                <div>
                                    <svg
                                        width="10px"
                                        height="10px"
                                        class="arrow-reversed"
                                        viewBox="0 0 5.9 17.51"
                                    >
                                        <use xlink:href="#icon-arrow-right-wide" />
                                    </svg>
                                </div>
                            </button>
                            <TransitionExpand>
                                <div
                                    class="transition-container"
                                    v-if="dropdowns.actionTaken"
                                    role="listbox"
                                >
                                    <button
                                        :key="option.value"
                                        v-for="option in actionTakenOpts"
                                        class="dropdown-choice"
                                        @click.stop="onActionTakenClick(option.value)"
                                        role="option"
                                    >
                                        {{ option.key }}
                                    </button>
                                </div>
                            </TransitionExpand>
                        </template>
                    </div>
                    <div v-if="showNumberValidityFilter" class="input-container" @click.stop>
                        <BaseSelect
                            ref="phoneNumberValidityFilter"
                            :modelValue="filter.invalidPhoneNumber"
                            :options="invalidPhoneOpts"
                            optionLabel="key"
                            optionValue="value"
                            label="Filter by Number Validity"
                            placeholder="Filter by Number Validity"
                            showClear
                            @update:modelValue="onValidPhoneClick"
                            hideLabel
                        />
                    </div>
                    <div v-if="showOptInStatusFilter" class="input-container" @click.stop>
                        <BaseSelect
                            ref="optInFilter"
                            :modelValue="filter.optedOutOfSms"
                            :options="invalidPhoneOpts"
                            optionLabel="key"
                            optionValue="value"
                            label="Filter by Opt-in Status"
                            placeholder="Filter by Opt-in Status"
                            showClear
                            @update:modelValue="onOptInClick"
                            hideLabel
                        />
                    </div>
                </div>
            </div>
            <div class="student-filters__actions">
                <Button
                    styleType="off"
                    v-if="showMoreFiltersButton"
                    v-bind:unbordered="true"
                    class="filter-button"
                    @click="toggleMoreFilters"
                    >{{ showMoreFilters ? 'Less Filters' : 'More Filters' }}</Button
                >
                <Button
                    v-if="hideClearButton != true"
                    styleType="off"
                    v-bind:unbordered="true"
                    class="filter-button"
                    @click="onFilterClear"
                    >Clear</Button
                >
            </div>
        </div>

        <div class="buttons-toolbar"></div>
    </div>
</template>

<script>
import { nextTick } from 'vue';
import { useRootStore } from '@/stores';
import TransitionExpand from '@/components-deprecated/TransitionExpand';
import Button from '@/components-deprecated/Button';
import { debounce } from '@/services/utils';
import DropdownV2 from '@/components-deprecated/global/v2/DropdownV2.vue';
import { isExperimentalAccessibilityEnabled } from '@/lib/feature-flag';
import v2CollectionManager from '@/services/v2CollectionManager';
import { useIsRetentionFeaturesEnabled } from '@/composables/useAdminStage';
import { BaseTreeSelect, BaseSelect, BaseInputText, BaseIcon } from '@edsights/ui-core';
import { RISK_LEVELS, RISK_CATEGORIES, RISK_SUB_DRIVERS } from '@/consts/risk';

// Constants
const INVALID_PHONE_OPTS = [
    { key: 'Yes', value: 'false' },
    { key: 'No', value: 'true' }
];

const ACTION_TAKEN_OPTS = [
    { key: 'Yes', value: 'true' },
    { key: 'No', value: 'false' }
];

export default {
    name: 'StudentAnalysisFilter',
    components: {
        DropdownV2,
        TransitionExpand,
        Button,
        BaseTreeSelect,
        BaseSelect,
        BaseInputText,
        BaseIcon
    },
    setup() {
        const rootStore = useRootStore();
        const enableRetentionFeatures = useIsRetentionFeaturesEnabled();
        return { rootStore, enableRetentionFeatures };
    },
    data() {
        return {
            showMoreFilters: false,
            tagCategories: v2CollectionManager.create({
                listType: this.useSVSTags ? 'svsTagCategories' : 'tagCategories',
                filters: {
                    schoolId: this.rootStore.user.schoolRef.id
                },
                errorHandlerOptions: {
                    enableAlert: true,
                    rethrow: false
                }
            }),
            invalidPhoneOpts: INVALID_PHONE_OPTS,
            actionTakenOpts: ACTION_TAKEN_OPTS,
            isLoadingTags: false,
            filter: this.getInitialFilter(),
            dropdowns: {
                intervention: false,
                actionTaken: false
            },
            executeFilter: () => {},
            riskLevels: Object.values(RISK_LEVELS).map((level) => ({
                name: level.label,
                id: level.value
            })),
            riskDrivers: Object.values(RISK_CATEGORIES).map((category) => ({
                name: category.label,
                id: category.value.toLowerCase()
            })),
            riskSubDrivers: RISK_SUB_DRIVERS
        };
    },
    props: {
        enableAutoScroll: {
            type: Boolean,
            default: false
        },
        hideClearButton: {
            type: Boolean,
            default: false
        },
        useSVSTags: {
            type: Boolean,
            default: false
        },
        showStudentSearch: {
            type: Boolean,
            default: true
        },
        showTagFilter: {
            type: Boolean,
            default: true
        },
        showRiskLevelFilter: {
            type: Boolean,
            default: true
        },
        showRiskDriverFilter: {
            type: Boolean,
            default: true
        },
        showRiskSubDriverFilter: {
            type: Boolean,
            default: false
        },
        showNumberValidityFilter: {
            type: Boolean,
            default: true
        },
        showOptInStatusFilter: {
            type: Boolean,
            default: true
        },
        showActionTakenFilter: {
            type: Boolean,
            default: false
        },
        showMoreFiltersButton: {
            type: Boolean,
            default: true
        },
        // Set to true to allow 1 tag selection at a time, otherwise multiple can be selected.
        singleSelectTags: {
            type: Boolean,
            default: false
        },
        initialTags: {
            type: Array,
            default: () => [],
            validator: (value) => {
                if (!Array.isArray(value)) {
                    return false;
                }

                for (const item of value) {
                    if (
                        typeof item !== 'object' ||
                        item === null ||
                        typeof item.id !== 'number' ||
                        typeof item.name !== 'string'
                    ) {
                        return false;
                    }
                }

                return true;
            }
        }
    },
    async created() {
        this.isLoadingTags = true;
        await this.tagCategories.refresh();
        if (this.initialTags.length > 0) {
            this.filter.tags = [...this.initialTags];
            if (this.enableAutoScroll) {
                this.scrollToBottomIfScrollable();
            }
        }
        this.isLoadingTags = false;
        this.executeFilter = debounce(this.executeFilterUnbounced, 700);

        window.addEventListener('click', this.closeEvent);
    },
    computed: {
        tagCategoriesList() {
            if (typeof this.tagCategories === 'object' && Array.isArray(this.tagCategories.list)) {
                return this.tagCategories.list;
            }
            return [];
        },
        isExperimentalAccessibilityEnabled() {
            if (this.rootStore.user.schoolRef) {
                return isExperimentalAccessibilityEnabled(this.rootStore.user.schoolRef.id);
            }

            return false;
        },
        filteredRiskSubDrivers() {
            // If no risk driver is selected or "Overall" is selected, return empty array
            if (!this.filter.riskDriver || this.filter.riskDriver.id === 'overall') {
                return [];
            }

            // Filter subdrivers based on the selected parent risk driver
            return this.riskSubDrivers.filter((subDriver) => {
                return subDriver.parent === this.filter.riskDriver.id;
            });
        },
        riskDriversTreeData() {
            return this.riskDrivers
                .filter((driver) => driver.id !== 'overall')
                .map((driver) => ({
                    key: driver.id,
                    label: driver.name,
                    children: this.riskSubDrivers
                        .filter((subDriver) => subDriver.parent === driver.id)
                        .map((subDriver) => ({
                            key: subDriver.id,
                            label: subDriver.name,
                            parentId: driver.id
                        }))
                }));
        },
        selectedRiskValues() {
            const selected = {};
            if (this.filter.riskDriver) {
                selected[this.filter.riskDriver.id] = { checked: true, partiallyChecked: false };
            }
            if (this.filter.riskSubDriver) {
                selected[this.filter.riskSubDriver.id] = { checked: true, partiallyChecked: false };
            }
            return selected;
        },
        selectedTagValues() {
            const selected = {};
            if (this.filter.tags && this.filter.tags.length > 0) {
                this.filter.tags.forEach((tag) => {
                    selected[tag.id] = { checked: true, partiallyChecked: false };
                });
            }
            return selected;
        },
        activeFilterTags() {
            const tags = [];

            // Risk Level
            if (this.filter.riskLevel != null) {
                tags.push({
                    key: 'riskLevel',
                    label: `${this.getRiskLevelName(this.filter.riskLevel)} Risk`,
                    removeHandler: () => this.updateFilter('riskLevel', null)
                });
            }

            // Risk Driver
            if (this.filter.riskDriver != null) {
                tags.push({
                    key: 'riskDriver',
                    label: `${this.filter.riskDriver.name} Risk Driver`,
                    removeHandler: () => this.onRiskDriverRemove()
                });
            }

            // Risk Sub Driver
            if (this.filter.riskSubDriver != null) {
                tags.push({
                    key: 'riskSubDriver',
                    label: `${this.filter.riskSubDriver.name} Risk Sub Driver`,
                    removeHandler: () => this.onRiskSubDriverRemove()
                });
            }

            // Opted Out of SMS
            if (this.filter.optedOutOfSms != null) {
                const option = this.invalidPhoneOpts.find(
                    (opt) => opt.value === this.filter.optedOutOfSms
                );
                tags.push({
                    key: 'optedOutOfSms',
                    label: `Opted In: ${option?.key || ''}`,
                    removeHandler: () => this.updateFilter('optedOutOfSms', null)
                });
            }

            // Invalid Phone Number
            if (this.filter.invalidPhoneNumber !== null) {
                const option = this.invalidPhoneOpts.find(
                    (opt) => opt.value === this.filter.invalidPhoneNumber
                );
                tags.push({
                    key: 'invalidPhoneNumber',
                    label: `Valid Phone: ${option?.key || ''}`,
                    removeHandler: () => this.updateFilter('invalidPhoneNumber', null)
                });
            }

            // Action Taken
            if (this.showActionTakenFilter && this.filter.actionTaken !== null) {
                const option = this.actionTakenOpts.find(
                    (opt) => opt.value === this.filter.actionTaken
                );
                tags.push({
                    key: 'actionTaken',
                    label: `Action Taken: ${option?.key || ''}`,
                    removeHandler: () => this.updateFilter('actionTaken', null)
                });
            }

            // Intervention
            if (this.filter.intervention != null) {
                tags.push({
                    key: 'intervention',
                    label: `Intervention ${this.filter.intervention.text}`,
                    removeHandler: () => this.updateFilter('intervention', null)
                });
            }

            return tags;
        },
        riskLevelFilterLabel() {
            // Show "Filter by Sub Risk Level" when a specific driver or subdriver is selected
            if (this.filter.riskSubDriver) {
                return 'Filter by Sub Risk Level';
            }

            if (this.filter.riskDriver && this.filter.riskDriver.id !== 'overall') {
                return 'Filter by Sub Risk Level';
            }

            return 'Filter by Risk Level';
        }
    },
    methods: {
        getInitialFilter() {
            return {
                searchText: '',
                tags: [],
                riskLevel: null,
                riskDriver: null,
                optedOutOfSms: null,
                invalidPhoneNumber: null,
                intervention: null,
                actionTaken: null,
                riskSubDriver: null
            };
        },
        updateFilter(field, value) {
            this.filter[field] = value;
            if (field === 'actionTaken') {
                this.dropdowns.actionTaken = false;
            } else if (field === 'intervention') {
                this.dropdowns.intervention = false;
            }
            this.executeFilter();
        },
        scrollToBottomIfScrollable() {
            if (!this.enableAutoScroll) return;
            nextTick(() => {
                const container = this.$refs.selectedFiltersContainer;
                if (container && container.scrollHeight > container.clientHeight) {
                    container.scrollTop = container.scrollHeight;
                }
            });
        },
        shouldShowPins() {
            return (
                this.filter.tags.length > 0 ||
                this.filter.riskLevel ||
                this.filter.riskDriver ||
                this.filter.riskSubDriver ||
                this.filter.optedOutOfSms != null ||
                this.filter.invalidPhoneNumber != null ||
                this.filter.intervention ||
                this.filter.actionTaken != null
            );
        },
        toggleMoreFilters() {
            this.showMoreFilters = !this.showMoreFilters;

            // If the menu was closed, we're done here.
            if (this.showMoreFilters === false) {
                return;
            }

            // Focus the "next" filter, which may be different depending on which filters
            // are enabled:
            nextTick(() => {
                if (this.$refs.actionTakenFilter) {
                    this.$refs.actionTakenFilter.focus();
                    return;
                }

                if (this.$refs.phoneNumberValidityFilter) {
                    this.$refs.phoneNumberValidityFilter.focus();
                    return;
                }

                if (this.$refs.optInFilter) {
                    this.$refs.optInFilter.focus();
                }
            });
        },
        onTagCategoriesNextPage() {
            this.tagCategories.addNextPage();
        },
        async onTagSearch(searchText) {
            if (searchText) {
                this.tagCategories.filters.search = searchText;
            } else {
                this.tagCategories.filters.search = null;
            }

            await this.tagCategories.refresh();
        },
        onSelectedTagRemoved(tag) {
            this.filter.tags = this.filter.tags.filter((t) => t.id !== tag.id);
            this.executeFilter();
        },
        onRiskLevelClick(riskLevel) {
            this.updateFilter('riskLevel', riskLevel);
        },
        onRiskDriverRemove() {
            this.onRiskTreeSelectChange({});
        },
        onRiskSubDriverRemove() {
            if (this.filter.riskDriver) {
                const driverOnly = {};
                driverOnly[this.filter.riskDriver.id] = { checked: true, partiallyChecked: false };
                this.onRiskTreeSelectChange(driverOnly);
            } else {
                this.onRiskTreeSelectChange({});
            }
        },
        onRiskTreeSelectChange(selectedValues) {
            // Clear existing selections
            this.filter.riskDriver = null;
            this.filter.riskSubDriver = null;

            if (selectedValues && typeof selectedValues === 'object') {
                // selectedValues is an object with keys as the selected IDs
                const selectedIds = Object.keys(selectedValues);

                for (const selectedId of selectedIds) {
                    // Check if it's a risk driver
                    const riskDriver = this.riskDrivers.find((driver) => driver.id === selectedId);
                    if (riskDriver) {
                        this.filter.riskDriver = riskDriver;
                    }

                    // Check if it's a risk sub driver
                    const riskSubDriver = this.riskSubDrivers.find(
                        (subDriver) => subDriver.id === selectedId
                    );
                    if (riskSubDriver) {
                        this.filter.riskSubDriver = riskSubDriver;
                        // Also set the parent driver if not already set
                        if (!this.filter.riskDriver) {
                            const parentDriver = this.riskDrivers.find(
                                (driver) => driver.id === riskSubDriver.parent
                            );
                            if (parentDriver) {
                                this.filter.riskDriver = parentDriver;
                            }
                        }
                    }
                }
            }

            this.executeFilter();
        },
        onTagTreeSelectChange(selectedValues) {
            // Clear existing tag selections
            this.filter.tags = [];

            if (selectedValues && typeof selectedValues === 'object') {
                const selectedIds = Object.keys(selectedValues);

                // Find all selected tags from all categories
                this.tagCategoriesList.forEach((category) => {
                    if (category.tagsRef) {
                        category.tagsRef.forEach((tag) => {
                            if (selectedIds.includes(tag.id.toString())) {
                                this.filter.tags.push(tag);
                            }
                        });
                    }
                });
            }

            this.executeFilter();
            if (this.enableAutoScroll) {
                this.scrollToBottomIfScrollable();
            }
        },
        onOptInClick(option) {
            this.updateFilter('optedOutOfSms', option);
        },
        onValidPhoneClick(option) {
            this.updateFilter('invalidPhoneNumber', option);
        },
        onActionTakenClick(option) {
            this.updateFilter('actionTaken', option);
        },
        onInterventionClick(option) {
            this.updateFilter('intervention', option);
        },
        onFilterClear() {
            this.filter = this.getInitialFilter();
            this.executeFilter();
        },
        executeFilterUnbounced() {
            const filter = {};

            // Search
            if (this.filter.search) {
                filter.search = this.filter.search;
            }

            // Tags
            if (this.filter.tags?.length > 0) {
                filter.tagIds = this.filter.tags.map((t) => t.id).join();
            }

            // Risk Level and Driver
            this.buildRiskFilter(filter);

            // Risk Sub Driver
            if (this.filter.riskSubDriver) {
                filter.riskSubDriver = this.filter.riskSubDriver.id;
            }

            // Boolean filters (convert string values back to booleans)
            if (this.filter.optedOutOfSms !== null) {
                filter.optedOutOfSms = this.filter.optedOutOfSms === 'true';
            }
            if (this.filter.invalidPhoneNumber !== null) {
                filter.invalidPhoneNumber = this.filter.invalidPhoneNumber === 'true';
            }

            // Intervention
            filter.intervention = this.filter.intervention?.id || null;

            // Action Taken (conditional)
            if (this.showActionTakenFilter && this.filter.actionTaken !== null) {
                filter.actionTaken = this.filter.actionTaken === 'true';
            }

            // Emit both the filter and the selected risk driver with subdriver info
            const selectedRiskInfo = {
                riskDriver: this.filter.riskDriver,
                riskSubDriver: this.filter.riskSubDriver,
                // For backwards compatibility, also include the main properties
                id: this.filter.riskDriver?.id,
                name: this.filter.riskDriver?.name
            };
            this.$emit('filterChanged', filter, selectedRiskInfo);
        },
        buildRiskFilter(filter) {
            if (this.filter.riskLevel) {
                const riskDriverId = this.filter.riskDriver?.id || 'overall';
                const riskDriverFilterName = `${riskDriverId}RiskLevel`;
                const riskLevelId =
                    typeof this.filter.riskLevel === 'object'
                        ? this.filter.riskLevel.id
                        : this.filter.riskLevel;
                filter[riskDriverFilterName] = riskLevelId;
            } else if (this.filter.riskDriver) {
                const riskDriverFilterName = `${this.filter.riskDriver.id}RiskLevel`;
                filter[riskDriverFilterName] = 'LOW_RISK,MEDIUM_RISK,HIGH_RISK';
            }
        },
        closeEvent() {
            this.closeDropdowns();
        },
        toggleDropdown(dropdownName) {
            this.dropdowns[dropdownName] = !this.dropdowns[dropdownName];
            if (this.dropdowns[dropdownName]) {
                this.closeDropdowns(dropdownName);
            }
        },
        closeDropdowns(activeDropdownName) {
            Object.keys(this.dropdowns)
                .filter((d) => !activeDropdownName || d !== activeDropdownName)
                .forEach((d) => (this.dropdowns[d] = false));
        },
        getRiskLevelName(riskLevel) {
            if (!riskLevel) return '';
            if (typeof riskLevel === 'object' && riskLevel.name) {
                return riskLevel.name;
            }
            // If it's a string ID, find the name from the options
            const foundLevel = this.riskLevels.find((level) => level.id === riskLevel);
            return foundLevel ? foundLevel.name : riskLevel;
        }
    }
};
</script>

<style lang="scss" scoped>
@import '~@/styles/variables';

.selected-filters-container {
    max-height: 150px;
    overflow-y: auto;
    margin-bottom: 1rem;
}

.student-filters {
    display: flex;
    align-items: center;
    flex-direction: column;
    align-items: flex-start;

    &__filters {
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        // flex: 1 0 auto;
    }

    &__actions {
        display: flex;
        // margin-left: auto;

        .button {
            margin: 0 0.5rem 0 0;
            min-width: 50px;
            padding: 0 0.5rem;
            color: $dark-gray;

            &:hover {
                color: $primary-brand-color;
            }
        }

        align-items: flex-end;

        > * {
            flex: 0 1 auto;
        }
    }
}

.col {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin: 1rem 0;
}

.row {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex: 1 0 auto;
    min-height: 35px;
    flex-wrap: wrap;
    margin-bottom: 5px;
}

.selected-tag {
    position: relative;
    border: 1px solid $dodger-blue;
    border-radius: 5px;
    padding: 0.2rem 0.75rem;
    margin-right: 1rem;
    width: 15rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.selected-tag-name {
    margin-right: 1rem;
}

.selected-tag-cancel {
    all: unset;
    cursor: pointer;
    position: absolute;
    top: 0.5rem;
    right: 0.25rem;
    width: 20px;
    height: 20px;
    display: flex;
    justify-content: center;
    border-radius: 4px;
    align-items: center;

    &:focus {
        outline: 2px solid $edsights-blue;
    }
}

.input-container {
    width: 230px;
    position: relative;
    align-items: center;
    margin-right: 2rem;
}

.filter-button {
    margin-right: 1rem;
    color: $accessible-gray;

    &:last-child {
        margin-right: 4rem;
    }
}

.input-group {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    background-color: $white-blue;
    padding: 0.25rem 1rem;
    border-radius: 5px;
    border: 0;
    min-height: 32px;
    width: 100%;
    color: $base-font-color;
    font-size: 12px;
    letter-spacing: 1px;

    &:focus {
        outline: 2px solid $edsights-blue;
    }

    > input {
        width: 100%;
        border: none;
        outline: none;
        background-color: $white-blue;
        padding: 0.25rem 3rem 0.25rem 1rem;
        border-radius: 5px;
        font: inherit;
        color: $input-gray;

        &:focus {
            outline: 2px solid $edsights-blue;
        }
    }

    .icon-search {
        position: absolute;
        right: 12px;
    }

    &.search-bar {
        padding: 0;
        background: transparent;
        border-radius: 5px;
        position: relative;
    }

    &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
        background-color: #f5f5f5;

        .input-label {
            color: #999;
        }
    }
}

.cursor-pointer {
    cursor: pointer;
}

.input-label {
    cursor: pointer;
    background-color: inherit;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 20rem;
}

.faded {
    color: $input-gray;
}

.clear-input-button {
    margin-right: 1rem;
}

.bordered {
    border: 1px solid $silver;
}

.arrow {
    transform: rotate(-90deg);

    &-reversed {
        transform: rotate(90deg);
    }
}

.icon-search {
    height: 15px;
    width: 15px;
    margin-right: 0;
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
}

.transition-container {
    position: absolute;
    z-index: 1000;
    box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.15);
    background-color: $white;
    left: 0;
    right: 0;
    max-height: 440px;
}

.dropdown {
    margin: 1rem;
}

.dropdown-choice {
    cursor: pointer;
    padding: 0.75rem 0.75rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    color: $base-font-color;
    font-size: 12px;
    letter-spacing: 1px;
    border: 0;
    background: #fff;
    text-align: left;
    margin-bottom: 2px;
    line-height: 26px;

    &:hover {
        background-color: $white-blue;
    }

    &:focus {
        outline: 2px solid $edsights-blue;
    }
}

.intervention-template-container {
    max-height: 300px;
    overflow-y: scroll;
    margin: 2rem 0rem;
}

.button-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    > :last-child {
        margin-left: 1rem;
    }
}

.buttons-toolbar {
    display: flex;
    justify-content: center;
}

:deep(.ui-core-base-select),
:deep(.ui-core-base-input-text),
:deep(.ui-core-base-tree-select) {
    width: 100% !important;
}
</style>
